import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import {
  AutomataService,
  Automation,
  AutomationInterface,
  ChoiceEnd,
  Choices,
  Group,
  OutputParameterInterface,
  RuleInterface,
  SleepWaitConditionsInterface,
  WaitForTriggerFilterInterface,
  WorkflowStep,
  WorkflowStepInterface,
} from '@vendasta/automata';
import { SnackbarService } from '@vendasta/galaxy/snackbar-service';
import { Observable, of, throwError } from 'rxjs';
import { catchError, switchMap, take, tap } from 'rxjs/operators';
import { AutomationViewService } from '../automation/automation-view.service';
import { TRIGGER_STEP_ID } from '../common/constants';
import { AutomationGraphUtilitiesService } from './automation-graph-utilities.service';
import { END_STEP_ID } from './automation-graph';
import { Branch, NONE, RIGHT } from './component-loader/branch-options';
import { TerminatingSteps } from './component-loader/constants';
import { AutomationErrorHandlerService } from './services';
import { getFieldViolationsFromError } from '@vendasta/shared';
import { v4 as uuidv4 } from 'uuid';
import { CurrentAutomationService } from '@galaxy/automata/shared';

@Injectable({ providedIn: 'root' })
export class WorkflowStepService {
  constructor(
    private readonly automationViewService: AutomationViewService,
    private readonly automationErrorHandlerService: AutomationErrorHandlerService,
    private readonly snackbarService: SnackbarService,
    private readonly translateService: TranslateService,
    private readonly graphUtilitiesService: AutomationGraphUtilitiesService,
    private readonly currentAutomationService: CurrentAutomationService,
    private readonly automataService: AutomataService,
  ) {}

  saveStep(step: WorkflowStepInterface): Observable<boolean> {
    return this.automataService.getAutomation(this.currentAutomationService.automationId).pipe(
      switchMap((a) => {
        const index = a.workflow.findIndex((s) => s.id === step.id);
        a.workflow[index] = new WorkflowStep(step);
        const fieldMask = {
          paths: ['Workflow'],
        };
        return this.automationViewService.update(a, fieldMask);
      }),
      tap(() => this.currentAutomationService.refreshAutomation()),
      take(1),
    );
  }

  saveGoal(step: WorkflowStepInterface): Observable<boolean> {
    return this.automataService.getAutomation(this.currentAutomationService.automationId).pipe(
      switchMap((a) => {
        a.goal = new WorkflowStep(step);
        const fieldMask = {
          paths: ['Goal'],
        };
        return this.automationViewService.update(a, fieldMask);
      }),
      tap(() => this.currentAutomationService.refreshAutomation()),
      take(1),
    );
  }

  private addStepAfterTrigger(a: AutomationInterface, newStepId: string): void {
    const triggerFiltersLength = a.triggerFilters?.length;
    if (triggerFiltersLength > 0) {
      a.triggerFilters[triggerFiltersLength - 1].choices.rules[0].next = newStepId;
      return;
    }
    a.triggerStep.choices.rules[0].next = newStepId;
  }

  private getNextTriggerStep(a: AutomationInterface): string {
    const triggerFiltersLength = a.triggerFilters?.length;
    if (triggerFiltersLength > 0) {
      return a.triggerFilters[triggerFiltersLength - 1].choices?.rules[0]?.next;
    }
    return a.triggerStep.choices?.rules[0]?.next;
  }

  public createChoices(
    rules: RuleInterface[],
    nextStep: string,
    subsequentStepToReplace = '',
    branch: Branch = NONE,
    defaultStep = '',
    isEndStep = false,
  ): Choices {
    if (typeof branch === 'number') {
      if (!rules || rules?.length === 0) {
        rules = [];
      }
      rules[branch].next = nextStep;
      return new Choices({
        defaultNextStep: defaultStep,
        rules: rules,
      });
    } else if (branch === RIGHT) {
      return new Choices({
        defaultNextStep: nextStep,
        rules: rules,
      });
    } else {
      if (isEndStep) {
        // so that any end step added will not connect to subsequent existing steps
        return new Choices({
          defaultNextStep: '',
        });
      }
      // Decision points update anything pointing at a merge step or that don't point to any step.
      const isADecisionPoint = rules?.length > 0;
      if (isADecisionPoint) {
        for (let i = 0; i < rules.length; i += 1) {
          // Point to the new step
          if (!rules[i].next || rules[i].next === subsequentStepToReplace) {
            rules[i].next = nextStep;
          }
        }
        const defaultNextStep = defaultStep === subsequentStepToReplace || !defaultStep ? nextStep : defaultStep;
        return new Choices({
          defaultNextStep: defaultNextStep,
          rules: rules,
        });
      }

      // Everything else is a simple update.
      return new Choices({
        defaultNextStep: nextStep,
      });
    }
  }

  private getSubsequentSteps(step: WorkflowStepInterface, branch: Branch): string[] {
    const options = new Set<string>();
    if (step.choices) {
      if (!!step.choices.rules && typeof branch === 'number') {
        options.add(step.choices.rules[branch].next);
      }
      if (!!step.choices.rules && branch === NONE) {
        for (const rule of step.choices.rules) {
          options.add(rule.next);
        }
      }
      if (!!step.choices.defaultNextStep && (branch === RIGHT || branch === NONE)) {
        options.add(step.choices.defaultNextStep);
      }
    }
    return Array.from(options).filter((option) => option !== step);
  }

  // adds the new step to the workflow, re-calculating where the steps should point at
  addStep(
    stepId: string,
    taskDefinitionId: string,
    data: string,
    sleep: number,
    sleepWaitConditions: SleepWaitConditionsInterface,
    waitForTriggerTaskDefinitionId: string,
    waitForTriggerFilter: WaitForTriggerFilterInterface,
    rules: RuleInterface[],
    precedingSteps: string[],
    branch: Branch,
    name?: string,
    customOutputParameters?: OutputParameterInterface[],
    idOverride?: string,
    notes?: string,
    groupId?: string,
  ): Observable<boolean> {
    const fieldMaskPaths = ['Workflow'];
    const stepAdded = this.addStepHelper(
      stepId,
      taskDefinitionId,
      data,
      sleep,
      sleepWaitConditions,
      waitForTriggerTaskDefinitionId,
      waitForTriggerFilter,
      rules,
      precedingSteps,
      branch,
      fieldMaskPaths,
      name,
      customOutputParameters,
      idOverride,
      notes,
      groupId,
    );

    if (!stepAdded) {
      return of(false);
    }

    const fieldMask = {
      paths: fieldMaskPaths,
    };

    return this.automationViewService.update(this.currentAutomationService.automation(), fieldMask).pipe(
      tap(() => this.currentAutomationService.refreshAutomation()),
      catchError((err) => {
        this.currentAutomationService.refreshAutomation();
        throw err;
      }),
      take(1),
    );
  }

  addSnippet(snippet: Automation, precedingSteps: string[], branch: Branch): Observable<boolean> {
    const fieldMask = {
      paths: ['Workflow'],
    };

    // Generate new IDs for the steps in the snippet
    this.generateNewStepIds(snippet);

    // Update the lastStep to point to the next step in the automation
    this.updateSnippetLastStepWithChoices(snippet, precedingSteps, branch);

    // Check if it's a trigger because a trigger needs it's own updating
    if (precedingSteps.length === 0) {
      this.updateTriggerWithSnippet(snippet, fieldMask);
    } else {
      // Update all the precedingSteps to point at the new snippet
      this.updatePrecedingStepsWithSnippet(snippet, precedingSteps, branch);
    }

    // Attach the new step to the workflow now that it's been assembled.
    const a = this.currentAutomationService.automation();
    a.workflow = (a.workflow || []).concat(...snippet.workflow);

    return this.automationViewService.update(a, fieldMask).pipe(
      tap(() => this.currentAutomationService.refreshAutomation()),
      take(1),
    );
  }

  private updateTriggerWithSnippet(snippet: Automation, fieldMask: any) {
    const a = this.currentAutomationService.automation();
    const firstStep = AutomationGraphUtilitiesService.getFirstStep(snippet.workflow);

    this.addStepAfterTrigger(a, firstStep.id);

    fieldMask.paths.push('TriggerStep', 'TriggerFilters');
  }

  private updatePrecedingStepsWithSnippet(snippet: Automation, precedingSteps: string[], branch: Branch) {
    const a = this.currentAutomationService.automation();

    const firstStep = AutomationGraphUtilitiesService.getFirstStep(snippet.workflow);

    precedingSteps.forEach((precedingStepId) => {
      const precedingStepIndex = a.workflow.findIndex((s) => s.id === precedingStepId);
      const precedingStep = a.workflow[precedingStepIndex];

      // Do not connect an end step to the new step - its next step id should remain as "" to end the automation
      if (TerminatingSteps.includes(precedingStep.taskDefinitionId)) {
        return;
      }

      // Otherwise update the preceding steps with the first step in the snippet
      precedingStep.choices = this.createChoices(
        precedingStep.choices.rules,
        firstStep.id,
        undefined,
        branch,
        precedingStep.choices?.defaultNextStep,
      );
      a.workflow[precedingStepIndex] = precedingStep;
    });
  }

  private updateSnippetLastStepWithChoices(snippet: Automation, precedingSteps: string[], branch: Branch) {
    const a = this.currentAutomationService.automation();
    const lastStep = AutomationGraphUtilitiesService.getLastStep(snippet.workflow);
    const lastStepIndex = snippet.workflow.findIndex((s) => s.id === lastStep.id);

    // Find the step we need to connect the snippet to
    let subsequentSteps: string[] = [];

    precedingSteps.forEach((precedingStepId) => {
      // Subsequent steps that matter are the intersection of all previous steps.
      const precedingStepIndex = a.workflow.findIndex((s) => s.id === precedingStepId);
      const precedingStep = a.workflow[precedingStepIndex];
      if (subsequentSteps.length === 0) {
        subsequentSteps = this.getSubsequentSteps(precedingStep, branch);
      } else {
        subsequentSteps = this.getSubsequentSteps(precedingStep, branch).filter((step) =>
          subsequentSteps.includes(step),
        );
      }
    });

    // This is the case where the snippet is being added after the trigger
    if (precedingSteps.length === 0) {
      subsequentSteps = [this.getNextTriggerStep(a)];
    }

    lastStep.choices = this.createChoices([], subsequentSteps[0]);
    snippet.workflow[lastStepIndex] = lastStep;
  }

  private generateNewStepIds(snippet: Automation) {
    // We need to do a stringify because the IDs can be in multiple places
    let stringifiedWorkflow = JSON.stringify(snippet.workflow);
    for (const step of snippet.workflow) {
      stringifiedWorkflow = stringifiedWorkflow.replaceAll(step.id, uuidv4());
    }
    snippet.workflow = JSON.parse(stringifiedWorkflow);
  }

  // adds the new step to the workflow, re-calculating where the steps should point at
  // returns true if the step was added successfully, false otherwise
  addStepHelper(
    stepId: string,
    taskDefinitionId: string,
    data: string,
    sleep: number,
    sleepWaitConditions: SleepWaitConditionsInterface,
    waitForTriggerTaskDefinitionId: string,
    waitForTriggerFilter: WaitForTriggerFilterInterface,
    rules: RuleInterface[],
    precedingSteps: string[],
    branch: Branch,
    fieldMaskPaths: string[],
    name?: string,
    customOutputParameters?: OutputParameterInterface[],
    idOverride?: string,
    notes?: string,
    groupId?: string,
  ): boolean {
    const a = this.currentAutomationService.automation();
    // Create a new step for the workflow if it exists.
    a.workflow = a.workflow || [];
    const newStep = this.buildNewStep(
      stepId,
      taskDefinitionId,
      data,
      sleep,
      sleepWaitConditions,
      waitForTriggerTaskDefinitionId,
      waitForTriggerFilter,
      name,
      customOutputParameters,
      notes,
      groupId,
    );

    // Added primarily to support automated testing.
    if (idOverride) {
      newStep.id = idOverride;
    }

    // If there's no preceding step this means that this step comes after a trigger.
    if (!precedingSteps || precedingSteps.length === 0) {
      // if the step added is an End action and there are subsequent steps it's invalid
      if (TerminatingSteps.includes(taskDefinitionId) && a.workflow.length > 0) {
        this.snackbarService.errorSnack(
          this.translateService.instant('AUTOMATIONS.EDITOR.ERRORS.CANT_ADD_TERMINATING_NODE_WITHIN_AUTOMATION'),
        );
        return false;
      }
      newStep.choices = this.createChoices(rules, this.getNextTriggerStep(a), NONE);
      this.addStepAfterTrigger(a, newStep.id);
      fieldMaskPaths.push('TriggerStep', 'TriggerFilters');
    }
    // Otherwise, this is a mid-workflow step or terminal step.
    else {
      // TODO: we have a graphing lib we use (AutomationGraphUtilities) we probably shouldn't be doing this manually
      // Identify common subsequent steps.
      // An inserted step will require preceding steps to point to it.
      // E.g. if A and B point to C and we're inserting D before C, then A and B need to point to D.
      let subsequentSteps: string[] = [];
      precedingSteps.forEach((precedingStepId) => {
        // Subsequent steps that matter are the intersection of all previous steps.
        const precedingStepIndex = a.workflow.findIndex((s) => s.id === precedingStepId);
        const precedingStep = a.workflow[precedingStepIndex];
        if (subsequentSteps.length === 0) {
          subsequentSteps = this.getSubsequentSteps(precedingStep, branch);
        } else {
          subsequentSteps = this.getSubsequentSteps(precedingStep, branch).filter((step) =>
            subsequentSteps.includes(step),
          );
        }
      });

      // There should only ever be one common subsequent step.
      const commonSubsequentStep = subsequentSteps[0] || '';

      // Tell all existing preceding steps that they now connect to a new step
      // If they also pointed at the common subsequent step, that needs to be updated.
      precedingSteps.forEach((precedingStepId) => {
        const precedingStepIndex = a.workflow.findIndex((s) => s.id === precedingStepId);
        const precedingStep = a.workflow[precedingStepIndex];
        // Do not connect an end step to the new step - its next step id should remain as "" to end the automation
        if (!TerminatingSteps.includes(precedingStep.taskDefinitionId)) {
          precedingStep.choices = this.createChoices(
            precedingStep.choices.rules,
            newStep.id,
            commonSubsequentStep,
            branch,
            precedingStep.choices?.defaultNextStep,
          );
          a.workflow[precedingStepIndex] = precedingStep;
        }
      });

      // Finally, the new step needs to point to any common subsequent step (or nothing at all)
      const isEndStep = TerminatingSteps.includes(newStep.taskDefinitionId);
      newStep.choices = this.createChoices(rules, commonSubsequentStep, '', 'NONE', ChoiceEnd, isEndStep);
    }

    // Attach the new step to the workflow now that it's been assembled.
    a.workflow.push(newStep);

    return true;
  }

  buildNewStep(
    stepId: string,
    taskDefinitionId: string,
    data: string,
    sleep: number,
    sleepWaitConditions: SleepWaitConditionsInterface,
    waitForTriggerTaskDefinitionId: string,
    waitForTriggerFilter: WaitForTriggerFilterInterface,
    name: string,
    customOutputParameters: OutputParameterInterface[],
    notes?: string,
    groupId?: string,
  ): WorkflowStep {
    return new WorkflowStep({
      id: stepId,
      taskDefinitionId: taskDefinitionId,
      data: data,
      sleep: sleep,
      sleepWaitConditions: sleepWaitConditions,
      waitForTriggerTaskDefinitionId: waitForTriggerTaskDefinitionId,
      waitForTriggerFilter: waitForTriggerFilter,
      choices: {},
      name: name,
      customOutputParameters: customOutputParameters,
      notes: notes,
      groupId: groupId,
    });
  }

  /**
   * If the automation's trigger points to the step being deleted, update the trigger.
   * @param triggerStep
   * @param previousSteps
   */
  replaceDeletedStepInTriggerIfRequired(triggerStep: WorkflowStep, deletedStep: WorkflowStepInterface): WorkflowStep {
    // If we're deleting the step right after the trigger, point the trigger to the step following this one.
    // Since we can't delete branches with children, this will always be the default step.
    if (triggerStep.choices?.rules[0].next === deletedStep.id) {
      // Note: Assumes that branches with different children cannot be deleted.
      const subsequentStepId =
        deletedStep.choices.defaultNextStep || (deletedStep.choices.rules ? deletedStep.choices.rules[0]?.next : '');
      triggerStep.choices.rules[0].next = subsequentStepId;
    }
    return triggerStep;
  }

  /**
   * If the automation's trigger's filters point to the step being deleted, update the filters.
   * @param triggerFilters
   * @param currentStep
   * @param precedingSteps
   */
  replaceDeletedStepInTriggerFiltersIfRequired(
    triggerFilters: WorkflowStep[],
    deletedStep: WorkflowStepInterface,
  ): WorkflowStep[] {
    // Replace the deleted step if it appears in filters.
    // Note: Assumes that branches with different children cannot be deleted.
    const subsequentStepId =
      deletedStep.choices.defaultNextStep || (deletedStep.choices.rules ? deletedStep.choices.rules[0]?.next : '');
    (triggerFilters || [])
      .filter((filter) => filter.choices?.defaultNextStep === deletedStep.id)
      .forEach((filter) => {
        filter.choices.defaultNextStep = subsequentStepId;
      });
    (triggerFilters || [])
      .filter((filter) => !!filter.choices?.rules)
      .forEach((filter) =>
        filter.choices.rules.forEach((rule) => {
          if (rule.next === deletedStep.id) {
            rule.next = subsequentStepId;
          }
        }),
      );
    return triggerFilters;
  }

  /**
   * Replaces the deleted step wherever it appears in the workflow.
   */
  replaceDeletedStepInWorkflow(
    a: Automation,
    deletedStep: WorkflowStepInterface,
    precedingSteps: string[],
  ): WorkflowStep[] {
    // Note: Assumes that branches with different children cannot be deleted.
    let subsequentStepId =
      deletedStep.choices.defaultNextStep || (deletedStep.choices.rules ? deletedStep.choices.rules[0]?.next : '');
    if (TerminatingSteps.includes(deletedStep.taskDefinitionId)) {
      // if the deletedStep is an End action then the subsequent step needs to be recalculated based on the graph
      subsequentStepId = this.graphUtilitiesService.findNextAction(deletedStep.id || '');
      // Empty string denotes the end step here, quick conversion from the graphV2 const
      subsequentStepId = subsequentStepId === END_STEP_ID ? '' : subsequentStepId;
    }

    const contains = (steps: WorkflowStep[], target: string): boolean => {
      return !!steps && steps.findIndex((s) => s.id === target) > -1;
    };

    // Sanity check: if any preceding step is unknown, something's gone wrong and we should avoid deleting.
    for (const previous of precedingSteps) {
      if (previous !== TRIGGER_STEP_ID && !contains(a.triggerFilters, previous) && !contains(a.workflow, previous)) {
        return a.workflow;
      }
    }

    // Point all preceding steps at the subsequent step, if appropriate.
    precedingSteps.forEach((previous) => {
      let previousStep;
      if (previous === TRIGGER_STEP_ID) {
        // This is handled in another function.
        // We simply need to delete the step.
        return;
      } else if (contains(a.triggerFilters, previous)) {
        // This is handled in another function.
        // We simply need to delete the step.
        return;
      } else if (contains(a.workflow, previous)) {
        const previousStepIndex = a.workflow.findIndex((s) => s.id === previous);
        previousStep = a.workflow[previousStepIndex];
      } else {
        // A non-existent step.
        return;
      }

      // The previous step may have branches, which may be pointing at the deleted step (e.g. branch with no children)
      if (previousStep.choices?.defaultNextStep === deletedStep.id) {
        previousStep.choices.defaultNextStep = subsequentStepId;
      }
      previousStep.choices?.rules?.forEach((rule) => {
        if (rule.next === deletedStep.id) {
          rule.next = subsequentStepId;
        }
      });
    });

    // Remove the deleted step from the workflow.
    const deletedStepIndex = a.workflow.findIndex((s) => s.id === deletedStep.id);
    if (deletedStepIndex > -1) {
      a.workflow.splice(deletedStepIndex, 1);
    }

    return a.workflow;
  }

  deleteMultipleActions(stepsToDelete: WorkflowStepInterface[]): Observable<boolean> {
    return this.automationViewService.get(this.currentAutomationService.automationId).pipe(
      switchMap((automation) => {
        const fieldMaskPaths = ['TriggerStep', 'TriggerFilters', 'Workflow', 'Groups'];
        let updatedWorkflow = [...automation.workflow];

        const isGroupExpanded = this.automationViewService.expandedGroups().has(stepsToDelete[0].groupId);
        const startNodeId = isGroupExpanded
          ? `expanded-group-start-${stepsToDelete[0].groupId}`
          : `group-${stepsToDelete[0].groupId}`;
        const endNodeId = isGroupExpanded
          ? `expanded-group-end-${stepsToDelete[0].groupId}`
          : `group-${stepsToDelete[0].groupId}`;

        const precedingNodes = this.graphUtilitiesService.findPreviousActions(startNodeId);
        let subsequentNodeId = this.graphUtilitiesService.findNextAction(endNodeId);

        subsequentNodeId = subsequentNodeId === END_STEP_ID ? '' : subsequentNodeId;

        const precedingSteps = precedingNodes
          .map((nodeId) => {
            return automation.workflow.find((step) => step.id === nodeId);
          })
          .filter(Boolean);

        if (precedingNodes[0] === TRIGGER_STEP_ID) {
          automation.triggerStep.choices.rules[0].next = subsequentNodeId;
        } else if (precedingSteps && precedingSteps[0].choices.rules?.length > 0 && stepsToDelete?.length > 1) {
          const allNodesInBranch = this.graphUtilitiesService.getAllNodesInBranch(precedingNodes[0]);

          if (precedingSteps[0].choices.defaultNextStep === stepsToDelete[0].id) {
            precedingSteps[0].choices.defaultNextStep =
              subsequentNodeId === END_STEP_ID || !allNodesInBranch.has(subsequentNodeId) ? '' : subsequentNodeId;
          }
          precedingSteps.forEach((step) => {
            step!.choices.rules.forEach((rule) => {
              if (stepsToDelete.some((deletedStep) => rule.next === deletedStep.id)) {
                rule.next = subsequentNodeId;
              }
            });
          });
        } else if (precedingSteps.length > 0) {
          precedingSteps.forEach((step) => {
            step!.choices.defaultNextStep = subsequentNodeId === END_STEP_ID ? '' : subsequentNodeId;
          });
        }

        // Remove the group and its steps from the workflow
        updatedWorkflow = updatedWorkflow.filter((step) => !stepsToDelete.some((s) => s.id === step.id));
        automation.groups = automation.groups.filter((group) => group.id !== stepsToDelete[0].groupId);
        automation.workflow = updatedWorkflow;

        return this.automationViewService.update(automation, { paths: fieldMaskPaths });
      }),
      tap(() => {
        this.currentAutomationService.refreshAutomation();
        stepsToDelete.forEach((step) => this.automationErrorHandlerService.deleteValidationError(step.id));
      }),
      catchError((err) => {
        const fieldViolations = getFieldViolationsFromError(err);
        const nameFieldError = fieldViolations?.find(
          (fv) => fv.field === 'Automation.WorkflowStep.Choices' || fv.field === 'Automation.WorkflowStep.Choices.Next',
        );
        const goalError = fieldViolations?.find((fv) => fv.field === 'Automation.Goal');
        if (goalError) {
          this.snackbarService.openErrorSnack('AUTOMATIONS.EDITOR.ERRORS.CANT_DELETE_BRANCH_BECAUSE_OF_GOAL_REFERENCE');
        } else if (nameFieldError || err.status === 400) {
          this.snackbarService.openErrorSnack('AUTOMATIONS.EDITOR.ERRORS.CANT_DELETE_BRANCH_REFERENCE');
        } else {
          this.snackbarService.openErrorSnack('COMMON.ERROR_TRY_AGAIN');
        }
        return throwError(err);
      }),
      take(1),
    );
  }

  deleteStep(stepToDelete: WorkflowStepInterface, precedingSteps: string[]): Observable<boolean> {
    return this.automationViewService.get(this.currentAutomationService.automationId).pipe(
      switchMap((a) => {
        const fieldMaskPaths = ['TriggerStep', 'TriggerFilters', 'Workflow'];
        // If all of the rules and the default step don't point to the same node then there is a step somewhere on one of the branches and we
        // can't delete it because it will orphan a step. Technically if the default next step is undefined and there is only one branch that has a step
        // on it then we could delete but it's not worth it to support that edge case
        if (stepToDelete.choices.rules?.length > 0) {
          const aRuleHasDifferentNextStep = stepToDelete.choices.rules.reduce((acc, curr) => {
            return acc || curr?.next !== stepToDelete.choices.defaultNextStep;
          }, false);
          if (aRuleHasDifferentNextStep) {
            this.snackbarService.errorSnack(
              this.translateService.instant('AUTOMATIONS.EDITOR.ERRORS.CANT_DELETE_BRANCH_V2'),
            );
            return of(false);
          }
        }

        // When a step is being deleted that is a trigger, there will only ever be one connection.
        a.triggerStep = this.replaceDeletedStepInTriggerIfRequired(a.triggerStep, stepToDelete);
        a.triggerFilters = this.replaceDeletedStepInTriggerFiltersIfRequired(a.triggerFilters, stepToDelete);
        a.workflow = this.replaceDeletedStepInWorkflow(a, stepToDelete, precedingSteps);

        const fieldMask = {
          paths: fieldMaskPaths,
        };
        return this.automationViewService.update(a, fieldMask);
      }),
      tap(() => {
        this.currentAutomationService.refreshAutomation();
        // remove validation error mapping entry for the deleted step
        this.automationErrorHandlerService.deleteValidationError(stepToDelete.id);
      }),
      catchError((err) => {
        const fieldViolations = getFieldViolationsFromError(err);
        const nameFieldError = fieldViolations?.find(
          (fv) => fv.field === 'Automation.WorkflowStep.Choices' || fv.field === 'Automation.WorkflowStep.Choices.Next',
        );
        const goalError = fieldViolations?.find((fv) => fv.field === 'Automation.Goal');
        if (goalError) {
          this.snackbarService.openErrorSnack('AUTOMATIONS.EDITOR.ERRORS.CANT_DELETE_BRANCH_BECAUSE_OF_GOAL_REFERENCE');
        } else if (nameFieldError || err.status === 400) {
          this.snackbarService.openErrorSnack('AUTOMATIONS.EDITOR.ERRORS.CANT_DELETE_BRANCH_REFERENCE');
        } else {
          this.snackbarService.openErrorSnack('COMMON.ERROR_TRY_AGAIN');
        }
        return throwError(err);
      }),
      take(1),
    );
  }

  deleteGoal(stepToDelete: WorkflowStepInterface): Observable<boolean> {
    return this.automationViewService.get(this.currentAutomationService.automationId).pipe(
      switchMap((a) => {
        const fieldMaskPaths = ['Goal'];
        a.goal = null;
        const fieldMask = {
          paths: fieldMaskPaths,
        };
        return this.automationViewService.update(a, fieldMask);
      }),
      tap(() => {
        this.currentAutomationService.refreshAutomation();
        // remove validation error mapping entry for the deleted step
        this.automationErrorHandlerService.deleteValidationError(stepToDelete.id);
      }),
      take(1),
    );
  }

  // addGroupToAutomation adds a new group to the automation
  // goes over the workflow and updates any steps with the new group
  addGroupToAutomation(
    a: Automation,
    groupName: string,
    stepsForGroup: Map<string, WorkflowStepInterface>,
  ): Observable<boolean> {
    const id = uuidv4();

    const automation = { ...a } as Automation;
    automation.workflow.forEach((step) => {
      if (stepsForGroup.has(step.id)) {
        step.groupId = id;
      }
    });

    automation.groups = automation.groups || [];
    automation.groups.push(new Group({ id: id, name: groupName }));

    return this.automationViewService
      .update(automation, { paths: ['Groups', 'Workflow'] })
      .pipe(tap(() => this.currentAutomationService.refreshAutomation()));
  }

  updateGroupDetails(a: Automation, group: Partial<Group>): Observable<boolean> {
    const groupIndex = a.groups.findIndex((g) => g.id === group.id);

    a.groups[groupIndex].name = group.name || '';
    a.groups[groupIndex].notes = group.notes || '';

    return this.automationViewService
      .update(a, { paths: ['Groups'] })
      .pipe(tap(() => this.currentAutomationService.refreshAutomation()));
  }

  removeGroupFromAutomation(a: Automation, groupID: string): Observable<boolean> {
    a?.workflow?.forEach((step) => {
      if (step.groupId === groupID) {
        step.groupId = '';
      }
    });

    a.groups = a.groups?.filter((group) => group.id !== groupID);

    return this.automationViewService
      .update(a, { paths: ['Groups', 'Workflow'] })
      .pipe(tap(() => this.currentAutomationService.refreshAutomation()));
  }

  getWorkflowStepsInGroup(a: Automation, groupID: string): WorkflowStep[] {
    return a.workflow.filter((step) => step.groupId === groupID);
  }
}
